import { useState } from 'react';
import { Wrench, Search, RotateCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfidenceMeter } from './ConfidenceMeter';
import { triageDepthLabel } from './triageLabels';
import { SummaryText } from './SummaryText';
import type { InvestigationAction } from '@/types/investigation';
import type { InvestigationWithTriage } from '@/queries/investigations';

export function ResultBody({
  investigation,
  onUseAsReply,
  onRunFullInvestigation,
  pending = false
}: {
  investigation: InvestigationWithTriage;
  onUseAsReply: (text: string) => void;
  onRunFullInvestigation?: () => void;
  pending?: boolean;
}) {
  const result = investigation.result;
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const triage = investigation.triage;
  // Deep runs (including a skipped-triage fallback, which forces deep) already got the full loop.
  const canRunFullInvestigation = Boolean(triage && triage.depth !== 'deep');

  if (!result) {
    return (
      <div data-testid="result-body" className="p-2 text-sm text-muted-foreground">
        No result available.
      </div>
    );
  }

  function toggle(i: number) {
    setTicked(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div data-testid="result-body" className="space-y-4 p-2">
      {triage && (
        <p className="text-xs text-muted-foreground">
          Triaged as {triageDepthLabel(triage.depth)} — {triage.reasoning}
        </p>
      )}

      <SummaryText text={result.summary} className="gap-2 text-sm" />

      <ConfidenceMeter value={result.confidence} />

      {canRunFullInvestigation && (
        <Button size="sm" variant="outline" disabled={pending} onClick={onRunFullInvestigation}>
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
          Run full investigation
        </Button>
      )}

      {result.actions.length > 0 && (
        <ul className="space-y-2">
          {result.actions.map((action, i) => (
            <ActionRow key={i} action={action} checked={ticked.has(i)} onToggle={() => toggle(i)} />
          ))}
        </ul>
      )}

      {result.draftReply && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-xs font-medium text-muted-foreground">Draft reply</p>
          <p className="whitespace-pre-wrap text-sm">{result.draftReply}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onUseAsReply(result.draftReply);
              toast.success('Draft inserted below — edit before sending.');
            }}
          >
            Use as reply
          </Button>
        </div>
      )}
    </div>
  );
}

function ActionRow({ action, checked, onToggle }: { action: InvestigationAction; checked: boolean; onToggle: () => void }) {
  const Icon = action.kind === 'resolve' ? Wrench : Search;

  return (
    <li className="flex items-start gap-2">
      <input type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={checked} onChange={onToggle} aria-label={`mark "${action.title}" done`} />
      <Icon data-testid={`action-icon-${action.kind}`} className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-sm font-semibold">{action.title}</p>
        <p className="text-xs text-muted-foreground">{action.detail}</p>
      </div>
    </li>
  );
}
